import moment from 'moment'
import { useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { faSquareDollar } from '@fortawesome/pro-regular-svg-icons'
import { faStars } from '@fortawesome/pro-solid-svg-icons'
import { useReselector } from '@/utils/sharedHooks'
import { DATE_FORMATS } from '@/utils/dateHelpers'
import { Alert, Button, Card, CardColors, Text } from '@/ui'
import { TypographyStyles, TextColors } from '@/styles'

import { DeadlineLabel } from '../DeadlineLabel'
import { SafeHarborPaymentCard } from './SafeHarborPaymentCard'
import { selectFederalEstimateByTaxQuarter } from '@/features/taxes/quarterly/userTaxEstimates.selector'
import { WhatIsSafeHarborPopup } from './helpers'
import { StateSafeHarborCard } from './StateSafeHarborCard'
import { selectQuarterDetailsForQuarterBeforeActiveQuarter } from '@/features/admin/quarterly-tax-estimate-details/quarterlyTaxEstimateDetails.selector'

const LastQuarterFederalSafeHarborCard = () => {
  const navigate = useNavigate()

  const lastQuarterDetails = useReselector(
    selectQuarterDetailsForQuarterBeforeActiveQuarter
  )

  return (
    <Card color={CardColors.LEVEL_1}>
      <div className="flex flex-col gap-4">
        <Text variant={TypographyStyles.EYEBROW} color={TextColors.MUTED}>
          important note
        </Text>

        <Text variant={TypographyStyles.H2}>
          Paying Q{lastQuarterDetails?.taxQuarter} Federal Quarterly Taxes with
          Safe Harbor
        </Text>

        <Text variant={TypographyStyles.BODY_SM}>
          The payment date for this quarter has passed. You can still use safe
          harbor to get an estimate that will help minimize federal underpayment
          penalties.
        </Text>

        <WhatIsSafeHarborPopup />

        <Alert
          title="Tip: It’s always better to pay something than nothing."
          customIconName={faStars}
        >
          Minimize how much you&apos;ll owe in interest by paying as soon as you
          can, even if it&apos;s not the full amount.
        </Alert>

        <Button
          onClick={() => {
            navigate('/taxes/quarterly/calculate-safe-harbor')
          }}
        >
          Calculate safe harbor
        </Button>
      </div>
    </Card>
  )
}

export const LastQuarterSafeHarborFlow = () => {
  const lastQuarterDetails = useReselector(
    selectQuarterDetailsForQuarterBeforeActiveQuarter
  )

  const federalTaxEstimate = useReselector(
    selectFederalEstimateByTaxQuarter,
    `${lastQuarterDetails?.taxYear}-${lastQuarterDetails?.taxQuarter}`
  )

  const federalContent = useMemo(() => {
    if (!lastQuarterDetails || !federalTaxEstimate) {
      return null
    }
    if (
      federalTaxEstimate.estimateInCents >= 0 &&
      federalTaxEstimate.safeHarborType
    ) {
      return <SafeHarborPaymentCard federalTaxEstimate={federalTaxEstimate} />
    } else {
      return <LastQuarterFederalSafeHarborCard />
    }
  }, [federalTaxEstimate, lastQuarterDetails])

  return (
    <div className="flex flex-col gap-6">
      <Text variant={TypographyStyles.H2}>Last Quarter&apos;s Estimates</Text>

      <DeadlineLabel
        icon={faSquareDollar}
        text="IRS payment due"
        prominentText={moment
          .utc(lastQuarterDetails?.irsPaymentDueAt)
          .format(DATE_FORMATS.DISPLAY_LONG)}
      />

      {federalContent}

      <StateSafeHarborCard forLastQuarter />
    </div>
  )
}
